"use client";
import React, { useState, useEffect, useId, useRef } from "react";
import DefaultImage from "@/mocks/assets/defaultImage.png";
import Image from "next/image";
import { RetailCart } from "@/models/cart/retailCart";
import { Item } from "@/dto/item";
import { v4 as uuidv4 } from "uuid";
import {
  CloseButton,
  Popover,
  PopoverButton,
  PopoverPanel,
} from "@headlessui/react";
import { DropdownArrowIcon } from "@/components/sortDropdown/dropdownArrowIcon";
import { PrimaryButton } from "@/components/button/primaryButton";
import { AgeRestricted } from "@/components/ageRestricted/ageRestricted";
import { usePathname } from "next/navigation";
import { QuantityStepper } from "@/components/quantityStepper/quantityStepper";
import { Modifier } from "@/dto/modifier";
import { ModifierGroup } from "@/dto/modifierGroup";
import { ModifierRuleBadge } from "@/components/modifierRuleBadge/modifierRuleBadge";
import { applyDataTestId } from "@/utils/testUtils";
import pom from "@/utils/pom";
import { useTranslations } from "next-intl";
import { CheckCircleIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { CartItemType, OrderType } from "@/constants/enum";
import {
  buildFinalSelectedModifiers,
  calculateModifiersTotal,
} from "@/utils/modifierUtils";
import { getDefaultItemAndOptions } from "@/utils/itemUtils";
import { useCurrency } from "@/app/providers/currencyContext";
import { useCartDrawer } from "@/contexts/CartDrawerContext";
import useMediaQuery from "@/utils/useMediaQuery";
import { useStickyOffset } from "@/utils/useStickyOffset";
import { useMerchantFeatureFlag } from "@/utils/useMerchantFeatureFlag";
import { FEATURE_FLAG_SHIPPING } from "@/utils/constants";
import {
  formatDimensions,
  formatDimensionsAriaLabel,
  formatWeight,
  isShippingAvailable,
  shouldShowShippingSpecifications,
  wouldExceedShippingCartItemLimit,
} from "@/utils/shippingUtils";
import { getFromSessionStorage } from "@/utils/sessionStorageUtils";
import { SessionStorageKey } from "@/utils/storageKeys";
import { ScheduledTime } from "@/dto/scheduledTime";
import { ShippingCartFullModal } from "@/components/shippingCartFullModal/shippingCartFullModal";

export const ItemPage: React.FC<any> = ({
  itemData,
  checkoutEnabled,
  isOpen,
  merchantColor,
  merchant,
}: any) => {
  const t = useTranslations("itemPage");
  const { format } = useCurrency();
  const { openCartDrawer } = useCartDrawer();

  const stickyContainerRef = useStickyOffset(20);

  const [selectedOptions, setSelectedOptions] = useState<any>({});
  const [specialInstructions, setSpecialInstructions] = useState<string>("");
  const [selectedModifiers, setSelectedModifiers] = useState<any>({});
  const [itemVariantUnavailable, setItemVariantUnavailable] =
    useState<boolean>(false);
  const [quantity, setQuantity] = useState<number>(1);
  const [totalPrice, setTotalPrice] = useState<number>(0);
  const [selectedItem, setSelectedItem] = useState<Item | null>(null);
  const [isPreview, setIsPreview] = useState<boolean>(false);
  const pathName = usePathname();
  const isShopPage: boolean = pathName?.includes("shop") || false;
  const [isAddtocartClicked, setIsAddtocartClicked] = useState<boolean>(false);
  const [isAdded, setIsAdded] = useState<boolean>(false);
  const [isCartFullModalOpen, setIsCartFullModalOpen] =
    useState<boolean>(false);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const titleId = useId();
  const isMobileScreen = useMediaQuery("(max-width: 768px)");
  const shippingEnabled = useMerchantFeatureFlag(
    merchant,
    FEATURE_FLAG_SHIPPING
  );
  const shippingAvailable = isShippingAvailable(merchant, shippingEnabled);

  useEffect(() => {
    setIsPreview(globalThis.location.hostname.startsWith("preview-"));
  }, []);

  useEffect(() => {
    if (titleRef.current) {
      titleRef.current.focus();
    }
  }, []);

  useEffect(() => {
    const handleCartClose = () => {
      setTimeout(() => {
        titleRef.current?.focus();
      }, 150);
    };

    globalThis.addEventListener("cart-close", handleCartClose);

    return () => {
      globalThis.removeEventListener("cart-close", handleCartClose);
    };
  }, []);

  useEffect(() => {
    if (isAdded && isMobileScreen && titleRef.current) {
      setTimeout(() => {
        titleRef.current?.focus();
      }, 1000);
    }
  }, [isAdded, isMobileScreen]);

  useEffect(() => {
    const { defaultItem, defaultOptions } = getDefaultItemAndOptions(
      itemData?.items,
      itemData?.variants
    );
    setSelectedItem(defaultItem);
    setSelectedOptions(defaultOptions);
  }, [itemData?.items, itemData?.variants]);

  useEffect(() => {
    if (itemData?.items) {
      const matchedItem = itemData.items.find((item: any) => {
        return item.options?.every((option: any) => {
          return Object.values(selectedOptions).includes(option.id);
        });
      });

      if (matchedItem) {
        setSelectedItem(matchedItem);
        setItemVariantUnavailable(false);
      } else {
        setSelectedItem(null);
        setItemVariantUnavailable(true);
      }
    }
  }, [selectedOptions, itemData?.items]);

  const selectableModifierGroups = Object.values(
    itemData?.modifierGroups ?? {}
  ).filter(
    (group: any) =>
      selectedItem?.modifierGroupIds.includes(group.id) &&
      group.modifiers?.length > 0 &&
      group.modifiers?.some((modifier: Modifier) => modifier.available)
  );

  useEffect(() => {
    if (selectedItem) {
      const basePrice = selectedItem.price;
      const modifiersTotal = calculateModifiersTotal(
        selectableModifierGroups,
        selectedModifiers
      );
      const updatedTotalPrice = (basePrice + modifiersTotal) * quantity;
      setTotalPrice(updatedTotalPrice);
    }
  }, [selectedModifiers, selectedItem, quantity, selectableModifierGroups]);

  const handleOptionChange = (variantId: string, optionId: string) => {
    handleQuantityChange(1);
    setSelectedOptions({
      ...selectedOptions,
      [variantId]: optionId,
    });
  };

  const handleModifierChange = (
    groupId: string,
    modifierId: string,
    isSingleSelection: boolean
  ) => {
    setIsAddtocartClicked(false);
    const group: ModifierGroup = itemData?.modifierGroups?.[groupId];
    if (!group) return;

    setSelectedModifiers((prev: any) => {
      const newSelectedModifiers = { ...prev };
      const groupSelections: { [key: string]: number } = {
        ...(newSelectedModifiers[groupId] ?? {}),
      };

      if (isSingleSelection) {
        newSelectedModifiers[groupId] = { [modifierId]: 1 };
        return newSelectedModifiers;
      }

      const isSelected: boolean = groupSelections[modifierId] !== undefined;

      if (isSelected) {
        delete groupSelections[modifierId];
      } else {
        const totalGroupQuantity: number = Object.values(
          groupSelections
        ).reduce((acc: number, quantity: number) => acc + quantity, 0);
        if (totalGroupQuantity < group.maxAllowed) {
          groupSelections[modifierId] = 1;
        }
      }
      newSelectedModifiers[groupId] = groupSelections;
      return newSelectedModifiers;
    });
  };

  const handleModifierQuantityChange = (
    groupId: string,
    modifierId: string,
    newQuantity: number
  ) => {
    setIsAddtocartClicked(false);
    const group: ModifierGroup = itemData?.modifierGroups?.[groupId];
    if (!group) return;

    if (newQuantity === 0) {
      setSelectedModifiers((prev: any) => {
        const newSelectedModifiers = { ...prev };
        if (newSelectedModifiers[groupId]?.[modifierId]) {
          delete newSelectedModifiers[groupId][modifierId];
        }
        return newSelectedModifiers;
      });
      return;
    }

    const groupSelections = selectedModifiers[groupId] ?? {};
    const otherModifiersQuantity: number = Object.keys(groupSelections).reduce(
      (acc, mId) => {
        if (mId !== modifierId) {
          return acc + groupSelections[mId];
        }
        return acc;
      },
      0
    );

    const availableQuantity: number = Math.min(
      group.maxAllowed - otherModifiersQuantity,
      99
    );

    if (newQuantity >= 1 && newQuantity <= availableQuantity) {
      setSelectedModifiers((prev: any) => {
        const newSelectedModifiers = JSON.parse(JSON.stringify(prev));
        if (newSelectedModifiers[groupId]?.[modifierId]) {
          newSelectedModifiers[groupId][modifierId] = newQuantity;
        }
        return newSelectedModifiers;
      });
    }
  };

  const handleQuantityChange = (newQuantity: number) => {
    if (newQuantity >= 1 && newQuantity <= Number.MAX_SAFE_INTEGER) {
      setQuantity(newQuantity);
    }
  };

  const validateMinimumSelections = () => {
    return selectableModifierGroups.every((group: any) => {
      const groupSelections: { [key: string]: number } =
        selectedModifiers[group.id] ?? {};
      const totalGroupQuantity: number = Object.values(groupSelections).reduce(
        (acc: number, quantity: number) => acc + quantity,
        0
      );
      return (
        totalGroupQuantity >= group.minRequired &&
        totalGroupQuantity <= group.maxAllowed
      );
    });
  };

  const handleAddToCart = () => {
    if (!validateMinimumSelections() || !selectedItem) {
      setIsAddtocartClicked(true);
      return;
    }

    const savedOrderType = getFromSessionStorage<Partial<ScheduledTime>>(
      SessionStorageKey.SCHEDULED_TIME,
      {}
    ).orderType;
    const shippingOrderSelected =
      shippingAvailable &&
      (savedOrderType === OrderType.SHIPPING || !savedOrderType);
    const cartLimitExceeded = wouldExceedShippingCartItemLimit({
      orderType: shippingOrderSelected
        ? OrderType.SHIPPING
        : savedOrderType ?? OrderType.PICKUP,
      cartItems: RetailCart.getItems(),
      requestedQuantity: quantity,
      maxItems: merchant.shippingPreferences?.maxItems,
    });

    if (cartLimitExceeded) {
      setIsCartFullModalOpen(true);
      return;
    }

    setIsAdded(true);
    const finalSelectedModifiers = buildFinalSelectedModifiers(
      selectedModifiers,
      itemData
    );

    const newItem = {
      type: CartItemType.RETAIL as const,
      id: selectedItem.id,
      name: selectedItem.name,
      price: selectedItem.price,
      quantity,
      modifiers: finalSelectedModifiers,
      specialInstructions,
      subtotal: totalPrice,
      total: totalPrice,
      cartId: uuidv4(),
      isAgeRestricted: selectedItem.isAgeRestricted,
      isShippable: selectedItem.isShippable,
      setIsAddtocartClicked,
    };

    RetailCart.addItem(newItem);
    openCartDrawer();
    setTimeout(() => {
      setQuantity(1);
      setSpecialInstructions("");
      setSelectedModifiers({});
      setIsAdded(false);
    }, 2000);
  };

  const productImage =
    selectedItem?.imageFilename ??
    (selectedItem?.images && selectedItem.images.length > 0
      ? selectedItem.images.reduce((prev: any, current: any) => {
          if (
            current.name.includes("xxxhdpi") ||
            (current.name.includes("xxhdpi") &&
              !prev.name.includes("xxxhdpi")) ||
            (current.name.includes("xhdpi") &&
              !prev.name.includes("xxhdpi") &&
              !prev.name.includes("xxxhdpi"))
          ) {
            return current;
          }
          return prev;
        }, selectedItem.images[0])
      : null);

  let disableAddButton: boolean =
    !selectedItem || selectedItem?.available === false || !isOpen || isAdded;

  const isAgeRestricted: boolean =
    selectedItem?.isAgeRestricted || itemData?.isAgeRestricted;

  const isUnavailable =
    selectedItem?.available === false ||
    (!isOpen && isShopPage && merchant.isService && merchant.isHybrid) ||
    itemVariantUnavailable;

  const translateMeasurement = (
    key: string,
    values?: Record<string, string | number>
  ) => t(key, values);
  const dimensions = formatDimensions(
    selectedItem?.dimensions,
    translateMeasurement
  );
  const dimensionsAriaLabel = formatDimensionsAriaLabel(
    selectedItem?.dimensions,
    translateMeasurement
  );
  const weight = formatWeight(selectedItem?.dimensions, translateMeasurement);
  const showSpecifications = shouldShowShippingSpecifications({
    merchant,
    shippingEnabled,
    dimensions,
    weight,
  });

  const getScreenReaderText = () => {
    const priceText = selectedItem
      ? format(selectedItem.price)
      : t("priceNotAvailable");
    const descriptionText =
      selectedItem?.description ?? itemData?.description ?? t("noDescription");
    const ageText = isAgeRestricted ? t("ageRestriction", { minAge: 18 }) : "";

    if (isAgeRestricted) {
      return t("fullProductSummary", {
        price: priceText,
        age: ageText,
        description: descriptionText,
      });
    }

    return t("priceAndDescription", {
      price: priceText,
      description: descriptionText,
    });
  };

  return (
    <>
      <ShippingCartFullModal
        isOpen={isCartFullModalOpen}
        maxItems={merchant.shippingPreferences?.maxItems ?? 0}
        merchantColor={merchantColor}
        onRequestClose={() => setIsCartFullModalOpen(false)}
      />
      <div className="flex flex-col items-center pt-6 w-full">
        <div className="flex flex-col sm:flex-row justify-center sm:gap-20 px-8 w-full">
          <div
            ref={stickyContainerRef}
            className="flex-shrink-0 w-full sm:w-[420px] sm:sticky sm:top-[var(--dynamic-sticky-top,20px)] sm:self-start"
          >
            <div
              className="relative overflow-hidden rounded-md bg-gray-200 mb-8"
              style={{ paddingBottom: "133.33%" }}
            >
              <Image
                width="0"
                height="0"
                sizes="100vw"
                alt={selectedItem?.name ?? itemData.name}
                src={productImage ? productImage.source : DefaultImage}
                className="absolute inset-0 w-full h-full object-cover"
                priority
              />
            </div>
          </div>
          <div className="sm:w-[40%] flex flex-col gap-5">
            <h1
              ref={titleRef}
              tabIndex={-1}
              id={titleId}
              data-testid={applyDataTestId(pom.itemPage.name)}
              className="text-4xl font-bold break-words pb-[2px]"
            >
              {selectedItem?.name ?? itemData?.name ?? t("unnamedItem")}
              {!isMobileScreen && (
                <span className="sr-only">{`${getScreenReaderText()}`}</span>
              )}
            </h1>
            <p
              data-testid={applyDataTestId(pom.itemPage.price)}
              className="text-[28px] font-semibold"
            >
              {selectedItem ? (
                <>
                  <span
                    className={`${
                      selectedItem.available ? "" : "line-through"
                    }`}
                  >
                    {format(selectedItem.price)}
                  </span>
                  {!selectedItem.available && (
                    <span className="ml-4">{t("soldOut")}</span>
                  )}
                </>
              ) : (
                t("priceNotAvailable")
              )}
            </p>
            {isAgeRestricted && (
              <AgeRestricted
                text={t("ageRestriction")}
                textClassName="text-[16px]"
                data-testid={applyDataTestId(
                  pom.itemPage.ageRestrictionWarning
                )}
                color={merchantColor}
              />
            )}
            <p
              data-testid={applyDataTestId(pom.itemPage.description)}
              className="text-[#637381] font-normal text-base break-words whitespace-pre-wrap"
            >
              {selectedItem?.description ??
                itemData?.description ??
                t("noDescription")}
            </p>

            {showSpecifications && (
              <section
                className="flex flex-col gap-3"
                data-testid="item-specifications"
                aria-labelledby="item-specifications-heading"
              >
                <h2
                  id="item-specifications-heading"
                  className="text-lg font-semibold leading-[30px]"
                >
                  {t("specifications")}
                </h2>
                <dl className="grid grid-cols-[auto_1fr] gap-x-10 gap-y-2 text-base">
                  {dimensions && (
                    <div className="contents">
                      <dt>{t("dimensions")}</dt>
                      <dd
                        aria-label={dimensionsAriaLabel ?? undefined}
                        data-testid="item-dimensions"
                      >
                        {dimensions}
                      </dd>
                    </div>
                  )}
                  {weight && (
                    <div className="contents">
                      <dt>{t("weight")}</dt>
                      <dd data-testid="item-weight">{weight}</dd>
                    </div>
                  )}
                </dl>
              </section>
            )}

            {itemData.variants?.map((variant: any) => {
              const dropdownId = `variant-dropdown-${variant.id}`;
              const selectedOptionName = variant.options.find(
                (option: any) => option.id === selectedOptions[variant.id]
              )?.name;
              return (
                <div
                  key={variant.id}
                  data-testid={applyDataTestId(pom.itemPage.variants)}
                  className="flex flex-col gap-[25px]"
                >
                  <label
                    htmlFor={dropdownId}
                    className="block font-semibold text-lg leading-[30px]"
                  >
                    {variant.name}
                  </label>
                  <Popover className="relative w-[242px]">
                    <PopoverButton
                      id={dropdownId}
                      disabled={isAdded}
                      aria-label={t("variantDropdownAriaLabel", {
                        variantName: variant.name,
                        selectedVariant: selectedOptionName ?? t("select"),
                      })}
                      className="flex items-center gap-x-2 w-full leading-6 text-gray-900 disabled:bg-grayDisabled/10"
                    >
                      <div
                        id={`item_data_variants_${variant.id}`}
                        className={`w-full h-[48px] flex flex-row justify-between items-center px-5 py-3 bg-white hover:bg-gray-50  border rounded-lg disabled:border-gray-300 ${
                          isAdded ? "border-gray-300" : "border-[#1F2A37]"
                        }`}
                      >
                        <p
                          className={clsx(
                            "min-w-0 flex-1 text-left truncate",
                            isAdded && "text-grayDisabled"
                          )}
                        >
                          {selectedOptionName ??
                            `${t("select")} ${variant.name}`}
                        </p>
                        <DropdownArrowIcon isDisabled={isAdded} />
                      </div>
                    </PopoverButton>
                    <PopoverPanel
                      transition
                      className="absolute  top-full z-20 w-full overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-gray-900/5 transition data-[closed]:translate-y-1 data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150 data-[enter]:ease-out data-[leave]:ease-in max-h-[560px] overflow-y-auto"
                    >
                      <div className="w-full">
                        {variant.options.map((option: any) => (
                          <CloseButton
                            key={option.id}
                            className="group w-full relative flex flex-row items-start gap-x-6 rounded-lg px-4 py-4 text-sm leading-6 hover:bg-gray-50 cursor-pointer"
                            onClick={() =>
                              handleOptionChange(variant.id, option.id)
                            }
                          >
                            <p className=""> {option.name}</p>
                          </CloseButton>
                        ))}
                      </div>
                    </PopoverPanel>
                  </Popover>
                </div>
              );
            })}
            {selectableModifierGroups.length > 0 && (
              <ModifierGroupDisplay
                {...{
                  selectableModifierGroups,
                  selectedItem,
                  selectedModifiers,
                  handleModifierChange,
                  handleModifierQuantityChange,
                  merchantColor,
                  isAddtocartClicked,
                  isAdded,
                }}
              />
            )}
            <div className="flex flex-col items-start">
              <label
                htmlFor="input-quantity"
                className="font-semibold text-lg pb-4 leading-6"
              >
                {t("quantity")}
              </label>
              <QuantityStepper
                handleQuantityChange={handleQuantityChange}
                disableInput={isAdded}
                disableMinus={isAdded}
                disablePlus={isAdded}
                quantity={quantity}
                min={1}
                adaName={itemData?.name}
                id={"input-quantity"}
                buttonClass="bg-white"
              />
            </div>
            <div className="flex flex-col gap-[25px] mb-[8px]">
              <label
                htmlFor="special-instructions-input"
                className="block text-lg font-semibold leading-[30px]"
              >
                {t("specialInstructions")}
              </label>
              <textarea
                id="special-instructions-input"
                className="w-full p-2 border rounded-md max-h-[500px] text-base font-medium leading-6 font-sans"
                value={specialInstructions}
                disabled={isAdded}
                maxLength={200}
                onChange={(e) => setSpecialInstructions(e.target.value)}
                placeholder={t("provideInfo")}
              />
            </div>
            {checkoutEnabled && !isPreview && (
              <PrimaryButton
                data-testid={applyDataTestId(pom.itemPage.addToCartButton)}
                style={{
                  backgroundColor:
                    disableAddButton && !isAdded ? "#C7C7C7" : undefined,
                }}
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                  if (disableAddButton || isAdded) {
                    e.preventDefault();
                    return;
                  }
                  handleAddToCart();
                }}
                className={`w-full text-white aria-disabled:text-black py-2 clover-button-primary rounded transition-all duration-200 ease-in-out relative ${
                  disableAddButton ? "cursor-not-allowed" : ""
                }`}
                aria-disabled={disableAddButton || isAdded}
              >
                <span
                  aria-hidden={!isUnavailable}
                  aria-label={t("itemUnavailableLabel")}
                  className={`flex items-center justify-center transition-opacity duration-200 ease-in-out ${
                    isUnavailable ? "text-black opacity-100" : "opacity-0"
                  }`}
                >
                  {t("itemUnavailable")}
                </span>

                <span
                  aria-hidden={!isAdded}
                  aria-label={t("itemAddedLabel")}
                  className={`flex items-center justify-center absolute inset-0 text-white transition-opacity duration-200 ease-in-out ${
                    isAdded ? "opacity-100" : "opacity-0"
                  }`}
                >
                  <CheckCircleIcon className="h-5 w-5 mr-2" />
                  <span>{t("added")}</span>
                </span>

                <span
                  aria-label={t("addItemLabel", {
                    itemName:
                      selectedItem?.name ?? itemData?.name ?? t("unnamedItem"),
                    itemPrice: selectedItem
                      ? format(selectedItem.price)
                      : t("priceNotAvailable"),
                  })}
                  aria-hidden={isUnavailable ?? isAdded}
                  className={`flex items-center justify-center absolute inset-0 transition-opacity duration-200 ease-in-out ${
                    !isUnavailable && !isAdded ? "opacity-100" : "opacity-0"
                  }`}
                >
                  {t("addToCart")}
                </span>
              </PrimaryButton>
            )}
          </div>
        </div>
        <div className="w-[96%] my-4 pb-4 border-b border-b-transparent"></div>
      </div>
    </>
  );
};

const ModifierGroupDisplay = ({
  selectableModifierGroups,
  selectedItem,
  selectedModifiers,
  handleModifierChange,
  handleModifierQuantityChange,
  merchantColor,
  isAddtocartClicked,
  isAdded,
}: any) => {
  useEffect(() => {
    if (isAddtocartClicked) {
      const firstUnmetGroup: any = selectableModifierGroups.find(
        (group: any) => {
          const groupSelections: { [key: string]: number } =
            selectedModifiers[group.id] ?? {};
          const totalGroupQuantity = Object.values(groupSelections).reduce(
            (acc: number, quantity: number) => acc + quantity,
            0
          );
          return !(
            totalGroupQuantity >= group.minRequired &&
            totalGroupQuantity <= group.maxAllowed
          );
        }
      );

      if (firstUnmetGroup) {
        const element = document.getElementById(firstUnmetGroup.id);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }
      }
    }
  }, [
    isAddtocartClicked,
    selectableModifierGroups,
    selectedItem,
    selectedModifiers,
  ]);

  return (
    <div className="flex flex-col gap-5">
      {selectableModifierGroups.map((group: any) => {
        const groupSelections: { [key: string]: number } =
          selectedModifiers[group.id] ?? {};
        const totalGroupQuantity: number = Object.values(
          groupSelections
        ).reduce((acc: number, quantity: number) => acc + quantity, 0);
        const isRequirementMet: boolean =
          totalGroupQuantity >= group.minRequired &&
          totalGroupQuantity <= group.maxAllowed;

        return (
          <div key={group.id} id={group.id} className="flex flex-col w-[100%]">
            <fieldset className={"flex flex-col"}>
              <legend className="w-full mb-[25px]">
                <div className="flex items-center justify-between">
                  <span
                    className="block text-lg font-semibold leading-[30px]"
                    data-testid={applyDataTestId(
                      pom.itemPage.modifierGroupName
                    )}
                  >
                    {group.name}
                  </span>
                  <ModifierRuleBadge
                    min={group.minRequired}
                    max={group.maxAllowed}
                    isRequirementMet={isRequirementMet}
                    isAddtocartClicked={isAddtocartClicked}
                    modifierGroupId={group.id}
                  />
                </div>
              </legend>
              <div className="flex flex-col gap-[25px]">
                <ModifierSelection
                  {...{
                    group,
                    selectedModifiers,
                    handleModifierChange,
                    handleModifierQuantityChange,
                    merchantColor,
                    groupSelections,
                    totalGroupQuantity,
                    isAdded,
                  }}
                />
              </div>
            </fieldset>
          </div>
        );
      })}
    </div>
  );
};

const ModifierSelection = ({
  group,
  handleModifierChange,
  handleModifierQuantityChange,
  merchantColor,
  groupSelections,
  totalGroupQuantity,
  isAdded,
}: any) => {
  const { format } = useCurrency();
  const isMaxReached: boolean = totalGroupQuantity >= group.maxAllowed;
  const t = useTranslations("itemPage");

  return (
    <>
      {group.modifiers.map((modifier: any) => {
        const inputId = `modifier-name-${modifier.id}`;
        const isSingleSelection: boolean =
          group.minRequired === 1 && group.maxAllowed === 1;
        const isSelected: boolean = !!groupSelections[modifier.id];
        const isDisabled: boolean =
          (!isSingleSelection && isMaxReached && !isSelected) ||
          !modifier.available ||
          isAdded;
        const modifierQuantity: number = groupSelections[modifier.id] ?? 1;
        const disablePlus: boolean =
          modifierQuantity >= Math.min(group.maxAllowed, 99) ||
          totalGroupQuantity >= group.maxAllowed;

        let textColor = "text-grayDark";

        if (isDisabled) {
          textColor = "text-grayDisabled";
        } else if (isSelected) {
          textColor = "text-black";
        }

        const isValidPrice =
          typeof modifier.price === "number" && !Number.isNaN(modifier.price);

        const price = isValidPrice
          ? format(modifier.price)
          : t("priceNotAvailable");

        return (
          <div
            key={modifier.id}
            className={clsx("w-full flex flex-col gap-[14px]", textColor, {
              "cursor-not-allowed": isDisabled,
            })}
            aria-disabled={isDisabled}
          >
            <div className="flex w-full">
              <div className="flex flex-1 items-center gap-[14px]">
                <label
                  tabIndex={-1}
                  htmlFor={inputId}
                  className={clsx(
                    "flex flex-1 flex-grow gap-[14px]",
                    textColor,
                    { "cursor-not-allowed": isDisabled }
                  )}
                >
                  {isSingleSelection && (
                    <input
                      id={inputId}
                      type="radio"
                      aria-label={t("selectModifierLabel", {
                        name: modifier.name,
                        price: price,
                      })}
                      name={group.id}
                      value={modifier.id}
                      checked={isSelected}
                      disabled={isDisabled}
                      onChange={() =>
                        handleModifierChange(
                          group.id,
                          modifier.id,
                          isSingleSelection
                        )
                      }
                      className="h-5 w-5 shrink-0 cursor-pointer disabled:cursor-not-allowed accent-[var(--merchant-color)]"
                      data-testid={
                        applyDataTestId(pom.itemPage.modifierInput) +
                        modifier.id
                      }
                    />
                  )}
                  {!isSingleSelection && (
                    <input
                      id={inputId}
                      type="checkbox"
                      aria-label={t("addModifierLabel", {
                        name: modifier.name,
                        price: price,
                      })}
                      name={group.id}
                      value={modifier.id}
                      checked={isSelected}
                      onChange={() =>
                        handleModifierChange(
                          group.id,
                          modifier.id,
                          isSingleSelection
                        )
                      }
                      disabled={isDisabled}
                      className="h-5 w-5 shrink-0 cursor-pointer disabled:cursor-not-allowed accent-[var(--merchant-color)]"
                      data-testid={
                        applyDataTestId(pom.itemPage.modifierInput) +
                        modifier.id
                      }
                    />
                  )}
                  <span className="text-sm text-left font-medium leading-5 flex-grow">
                    {modifier.name}
                  </span>
                </label>
                <span
                  aria-label={t("modifierPriceLabel", { price })}
                  data-testid={
                    applyDataTestId(pom.itemPage.modifierPrice) + modifier.id
                  }
                  className={clsx(
                    "text-right text-sm font-medium leading-[14px]",
                    textColor
                  )}
                >
                  {price}
                </span>
              </div>
            </div>
            {!isSingleSelection && isSelected && (
              <span className={`pl-7 flex flex-col gap-[14px]`}>
                <QuantityStepper
                  id={`stepper-${modifier.id}`}
                  handleQuantityChange={(newQuantity: number) =>
                    handleModifierQuantityChange(
                      group.id,
                      modifier.id,
                      newQuantity
                    )
                  }
                  quantity={modifierQuantity}
                  disableInput={isAdded}
                  disableMinus={isAdded}
                  disablePlus={disablePlus || isAdded}
                  max={group.maxAllowed}
                  totalQuantity={totalGroupQuantity}
                  modifierId={modifier.id}
                  adaName={modifier?.name}
                  isAdded={isAdded}
                  buttonClass="bg-white"
                />
              </span>
            )}
          </div>
        );
      })}
    </>
  );
};
